import { Link, useParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/customer/shell/PageHeader";
import { formatMoneyCents, formatDate } from "@/customer/portal/format";
import { useOrderDetail } from "@/customer/portal/api";

// Lands here after `Checkout::Finalize` succeeds. The page shows the
// order number + delivery date + payment status, then surfaces a
// brand-red CTA to sign the rental agreement (the agreement signer is
// order-bound today, so it lives on its existing route — embedding it
// inline pre-finalize is a follow-up).
export function CheckoutConfirmedPage(): JSX.Element {
  const { order_number = "" } = useParams<{ order_number: string }>();
  const query = useOrderDetail(order_number);

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Reservation confirmed"
        title="We've got your reservation"
        description={
          order_number ? (
            <>
              Your reservation number is{" "}
              <strong className="font-semibold text-brand-black">{order_number}</strong>
              . We&rsquo;ve emailed a copy too.
            </>
          ) : (
            "We've emailed your reservation details."
          )
        }
      />

      {query.isPending ? <BodySkeleton /> : null}
      {query.isError ? (
        <Alert variant="destructive">
          We couldn&rsquo;t load this order. Refresh the page or call (303) 435-4328.
        </Alert>
      ) : null}

      {query.data ? <ConfirmedBody data={query.data} /> : null}

      <Steps orderNumber={order_number} />
    </div>
  );
}

function ConfirmedBody({
  data
}: {
  data: ReturnType<typeof useOrderDetail>["data"] & object;
}): JSX.Element {
  const { order, lines, agreement, invoices } = data;
  const total = invoices.reduce((acc, inv) => acc + inv.total_cents, 0);

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_320px]">
      <section className="space-y-6">
        <Card title="Equipment">
          <ul className="divide-y divide-zinc-100">
            {lines.map((line) => (
              <li key={line.id} className="py-2 text-sm">
                <p className="font-medium text-brand-black">
                  {line.type_name ?? "Equipment"}
                </p>
                {line.addons.length > 0 ? (
                  <p className="mt-1 text-xs text-zinc-500">
                    {line.addons.map((a) => a.name).join(" · ")}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Delivery">
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <Field
              label="Delivery date"
              value={order.delivery_date ? formatDate(order.delivery_date) : "We'll confirm"}
            />
            <Field
              label="Pickup date"
              value={order.pickup_date ? formatDate(order.pickup_date) : "We'll confirm"}
            />
            <Field
              label="Site address"
              value={formatAddress(order.site_address)}
            />
          </dl>
        </Card>

        <Card title="Rental agreement">
          {agreement.signed ? (
            <p className="text-sm text-emerald-700">
              <span aria-hidden="true">✓ </span>
              Signed
              {agreement.signed_at ? <> on {formatDate(agreement.signed_at)}</> : null}.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600">
                You haven&rsquo;t signed your rental agreement yet. We need this
                before we can dispatch the equipment.
              </p>
              <Link
                to={`/checkout/agreement/${data.order.order_number}`}
                className="inline-flex items-center gap-2 rounded-md bg-brand-red px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-brand-white shadow hover:bg-brand-red-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
              >
                Sign rental agreement
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          )}
        </Card>
      </section>

      <aside className="space-y-4">
        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-brand-white shadow-sm">
          <div className="border-b border-zinc-100 bg-flag-navy px-5 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-white">
              Reservation total
            </h2>
          </div>
          <div className="space-y-3 p-5">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-zinc-600">Total</span>
              <span className="text-2xl font-semibold text-brand-black">
                {formatMoneyCents(total)}
              </span>
            </div>
            <p className="text-xs text-zinc-500">
              We&rsquo;ll contact you to capture payment. Your reservation is held
              while we wait.
            </p>
          </div>
        </div>
        <Link
          to="/portal"
          className="block rounded-md border border-zinc-200 bg-brand-white px-5 py-3 text-center text-sm font-medium text-brand-black hover:border-brand-red hover:text-brand-red"
        >
          View this order in your portal
        </Link>
      </aside>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-lg border border-zinc-200 bg-brand-white p-5 shadow-sm">
      <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-red">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-zinc-500">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-brand-black">{value}</dd>
    </div>
  );
}

function formatAddress(addr: {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
}): string {
  const parts = [addr.line1, addr.line2, addr.city && addr.state ? `${addr.city}, ${addr.state}` : null, addr.postal_code];
  return parts.filter(Boolean).join(" · ") || "—";
}

function Steps({ orderNumber }: { orderNumber: string }): JSX.Element {
  return (
    <section className="rounded-lg border border-zinc-200 bg-brand-white p-6 shadow-sm">
      <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-red">
        What happens next
      </h2>
      <ol className="mt-3 space-y-3 text-sm text-zinc-700">
        <NextStep n={1} title="Sign your rental agreement">
          <Link to={`/checkout/agreement/${orderNumber}`} className="text-brand-red underline">
            Sign now →
          </Link>
        </NextStep>
        <NextStep n={2} title="We&rsquo;ll contact you to capture payment">
          We reach out within one business day. ACH or check — your preference.
        </NextStep>
        <NextStep n={3} title="Delivery scheduled">
          Once your agreement is signed, your COI (if needed) is approved, and
          payment is captured, dispatch confirms the exact delivery window.
        </NextStep>
      </ol>
    </section>
  );
}

function NextStep({ n, title, children }: { n: number; title: React.ReactNode; children: React.ReactNode }): JSX.Element {
  return (
    <li className="flex gap-3">
      <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-flag-navy text-xs font-semibold text-brand-white">
        {n}
      </span>
      <div className="flex-1">
        <p className="font-semibold text-brand-black">{title}</p>
        <p className="mt-1 text-zinc-600">{children}</p>
      </div>
    </li>
  );
}

function BodySkeleton(): JSX.Element {
  return (
    <div className="space-y-3" aria-busy="true">
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}
